/* DuaaDoor V3 — global behaviors */

/* Reveal on scroll */
(() => {
  if (!('IntersectionObserver' in window)) {
    document.querySelectorAll('.dd-reveal').forEach(el => el.classList.add('is-in'));
    return;
  }
  const io = new IntersectionObserver((entries) => {
    entries.forEach(e => {
      if (e.isIntersecting) {
        e.target.classList.add('is-in');
        io.unobserve(e.target);
      }
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });

  const watch = () => document.querySelectorAll('.dd-reveal:not(.is-in)').forEach(el => io.observe(el));
  watch();
  document.addEventListener('shopify:section:load', watch);
})();

/* Sticky CTA reveal after hero */
(() => {
  const sticky = document.querySelector('[data-sticky-cta]');
  if (!sticky) return;
  const onScroll = () => {
    sticky.classList.toggle('is-visible', window.scrollY > 600);
  };
  onScroll();
  window.addEventListener('scroll', onScroll, { passive: true });
})();

/* Header scroll state */
(() => {
  const header = document.querySelector('[data-dd-header]');
  if (!header) return;
  let lastY = 0;
  const onScroll = () => {
    const y = window.scrollY;
    header.classList.toggle('is-scrolled', y > 24);
    header.classList.toggle('is-up', y < lastY || y < 80);
    lastY = y;
  };
  onScroll();
  window.addEventListener('scroll', onScroll, { passive: true });
})();

/* Mobile menu toggle */
(() => {
  const root = document.querySelector('[data-dd-header]');
  if (!root) return;
  const toggle = root.querySelector('[data-menu-toggle]');
  const drawer = root.querySelector('[data-menu-drawer]');
  if (!toggle || !drawer) return;
  const close = () => {
    root.classList.remove('menu-open');
    document.body.style.overflow = '';
    toggle.setAttribute('aria-expanded', 'false');
  };
  toggle.addEventListener('click', () => {
    const open = !root.classList.contains('menu-open');
    root.classList.toggle('menu-open', open);
    document.body.style.overflow = open ? 'hidden' : '';
    toggle.setAttribute('aria-expanded', String(open));
  });
  drawer.addEventListener('click', (e) => {
    if (e.target === drawer || e.target.closest('[data-menu-close]')) close();
  });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
})();

/* Cart drawer (open/close) */
(() => {
  const drawer = document.querySelector('[data-cart-drawer]');
  if (!drawer) return;
  let lastTrigger = null;

  const close = () => {
    if (document.activeElement && drawer.contains(document.activeElement)) {
      document.activeElement.blur();
    }
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    if (lastTrigger && typeof lastTrigger.focus === 'function') lastTrigger.focus();
  };
  const open = (trigger) => {
    lastTrigger = trigger || null;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  };

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-cart-open]');
    if (opener) { e.preventDefault(); open(opener); return; }
    if (!drawer.classList.contains('is-open')) return;
    if (e.target.closest('[data-cart-close]') || e.target.matches('[data-cart-overlay]')) {
      close();
    }
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawer.classList.contains('is-open')) close();
  });
})();

/* Variant selector + add to cart (product page) */
(() => {
  const form = document.querySelector('[data-product-form]');
  if (!form) return;

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const btn = form.querySelector('[data-add-btn]');
    const originalText = btn.textContent;
    btn.disabled = true;
    btn.textContent = '...';
    try {
      const formData = new FormData(form);
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
        body: formData
      });
      if (!res.ok) throw new Error('cart-add-failed');
      btn.textContent = '✓ Ajouté';
      const drawer = document.querySelector('[data-cart-drawer]');
      if (drawer) {
        drawer.classList.add('is-open');
        document.body.style.overflow = 'hidden';
        refreshCart();
      } else {
        window.location.href = '/cart';
      }
    } catch (err) {
      btn.textContent = 'Erreur — réessayez';
    } finally {
      setTimeout(() => { btn.disabled = false; btn.textContent = originalText; }, 1800);
    }
  });
})();

/* Offer pack AJAX add-to-cart (homepage + product page offer cards) */
(() => {
  document.addEventListener('click', async (e) => {
    const cta = e.target.closest('[data-pack-add]');
    if (!cta) return;
    const variantId = cta.dataset.variantId;
    if (!variantId) return;
    e.preventDefault();
    if (cta.classList.contains('is-loading')) return;

    const labelEl = cta.querySelector('.dd-offers__cta-label') || cta;
    const originalLabel = labelEl.textContent;
    cta.classList.add('is-loading');
    cta.setAttribute('aria-busy', 'true');
    labelEl.textContent = 'Ajout…';

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody.description || 'cart-add-failed');
      }
      labelEl.textContent = '✓ Ajouté';
      const drawer = document.querySelector('[data-cart-drawer]');
      if (drawer) {
        drawer.classList.add('is-open');
        drawer.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
        if (typeof refreshCart === 'function') refreshCart();
      } else {
        window.location.href = '/cart';
      }
    } catch (err) {
      labelEl.textContent = 'Erreur — réessayez';
    } finally {
      setTimeout(() => {
        cta.classList.remove('is-loading');
        cta.removeAttribute('aria-busy');
        labelEl.textContent = originalLabel;
      }, 1800);
    }
  });
})();

async function refreshCart() {
  try {
    const [drawerRes, cartRes] = await Promise.all([
      fetch('/?section_id=cart-drawer'),
      fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
    ]);
    const html = await drawerRes.text();
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const newDrawer = doc.querySelector('[data-cart-drawer]');
    const current = document.querySelector('[data-cart-drawer]');
    if (newDrawer && current) {
      current.innerHTML = newDrawer.innerHTML;
      current.classList.add('is-open');
      current.setAttribute('aria-hidden', 'false');
    }
    if (cartRes.ok) {
      const cart = await cartRes.json();
      document.querySelectorAll('[data-cart-count]').forEach(el => {
        el.textContent = cart.item_count;
      });
    }
  } catch (e) { /* silent */ }
}
window.refreshCart = refreshCart;
